import React, { useEffect, useState, useMemo } from 'react';
import { Card, Button, Spinner, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { FiDownload, FiAlertCircle, FiEye } from 'react-icons/fi';
import { fetchBillsList, downloadBill } from './billParsingSlice';
import { LoadingState } from '../../utilities/constants';
import { parseUserInfo, formatDate, formatApiErrorDates } from '../../utilities/common';
import DataTable from '../../components/DataTable';
import Empty from '../../components/Empty';
import EmissionDataModal from './components/EmissionDataModal';

const HELP_DESK_URL = 'https://support.simblegroup.com/hc/en-us/requests/new';
const UNRECOGNISED_PROVIDER_ERROR = 'provider could not be identified';
const SCANNED_PDF_ERROR_KEY =
  'This file appears to be a scanned PDF or the document structure is not recognised. If this is a native PDF invoice, please submit it to the Simble team via our <helpdesk>Help Desk</helpdesk>. We will review the file and get back to you as soon as possible.';

const renderScannedPdfErrorMessage = () => (
  <Trans
    i18nKey={SCANNED_PDF_ERROR_KEY}
    components={{
      // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans injects the link text at runtime
      helpdesk: <a href={HELP_DESK_URL} target="_blank" rel="noreferrer" />
    }}
  />
);

const statusBadgeStyle = {
  fontSize: '11px',
  padding: '3px 9px',
  borderRadius: '20px',
  fontWeight: 500,
  display: 'inline-block',
  whiteSpace: 'nowrap',
  background: '#f8d7da',
  color: '#842029'
};

const UploadedBillsTab = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { orgId } = parseUserInfo(useSelector((state) => state.users.user));
  const { listStatus, bills = [] } = useSelector((state) => state.billParsing);
  const [currentPage] = useState(1);
  const [pageSize] = useState(20);
  const [errorMessage, setErrorMessage] = useState(null);

  const viewBillId = new URLSearchParams(location.search).get('viewBill');
  const viewingBill = viewBillId ? bills.find((b) => String(b.upldflId) === viewBillId) : null;

  const closeBillView = () => history.replace({ search: '' });

  useEffect(() => {
    dispatch(fetchBillsList({ orgId, page: currentPage, limit: pageSize }));
  }, [dispatch, orgId, currentPage, pageSize]);

  const tableColumns = useMemo(
    () => [
      {
        Header: t('File Name'),
        accessor: 'upldflName'
      },
      {
        Header: t('Upload Status'),
        accessor: 'upldflStatus',
        Cell: ({ value }) =>
          value === 'PROCESSING_FAILED' ? <span style={statusBadgeStyle}>{value}</span> : value
      },
      {
        Header: t('Errors'),
        accessor: 'upldflStatusMessage',
        Cell: ({ row, value }) => {
          if (!value || row.original.upldflStatus === 'SUCCESSFUL') return null;
          return (
            <Button variant="link" onClick={() => setErrorMessage(value)}>
              <FiAlertCircle style={{ color: '#f00808' }} className="ml-1" />
            </Button>
          );
        }
      },
      {
        Header: t('Uploaded By'),
        accessor: 'upldflCreatedBy'
      },
      {
        Header: t('Uploaded Date'),
        accessor: 'upldflCreatedDate',
        Cell: ({ value }) => formatDate(value)
      },
      {
        Header: t('Actions'),
        Cell: ({ row }) => (
          <>
            {row.original.upldflStatus === 'PROCESSED' && (
              <Button
                variant="link"
                size="sm"
                onClick={() => history.replace({ search: `?viewBill=${row.original.upldflId}` })}
                title={t('View')}
              >
                <FiEye />
              </Button>
            )}
            <Button
              variant="link"
              size="sm"
              onClick={() => dispatch(downloadBill({ orgId, fileId: row.original.upldflId }))}
              title={t('Download')}
            >
              <FiDownload />
            </Button>
          </>
        )
      }
    ],
    [t, dispatch, orgId, history]
  );

  if (listStatus === LoadingState.pending) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (listStatus === LoadingState.failed) {
    return <Empty title={t('Failed to load bills')} />;
  }

  if (bills.length === 0) {
    return <Empty title={t('No bills uploaded yet')} description={t('Click Upload & Parse tab to get started')} />;
  }

  return (
    <>
      <Card>
        <Card.Body>
          <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
            {t('Manage and review your previously uploaded utility bills')}
          </p>
          <DataTable columns={tableColumns} data={bills} defaultSort="upldflCreatedDate" />
        </Card.Body>
      </Card>
      <Modal show={errorMessage !== null} onHide={() => setErrorMessage(null)}>
        <Modal.Header>
          <Modal.Title>{t('Error Details')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorMessage && errorMessage.toLowerCase().includes(UNRECOGNISED_PROVIDER_ERROR)
            ? renderScannedPdfErrorMessage()
            : formatApiErrorDates(errorMessage)}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setErrorMessage(null)}>
            {t('Close')}
          </Button>
        </Modal.Footer>
      </Modal>
      {viewingBill && <EmissionDataModal bill={viewingBill} onClose={closeBillView} />}
    </>
  );
};

export default UploadedBillsTab;
